/* Collection filtering, sorting, and load-more (§4.11).
   Two filter surfaces share one state: inline dropdowns on desktop
   (apply on change) and a slide-in panel on mobile (explicit Apply).
   Applying builds a query from the active viewport's inputs, fetches the
   same section via the Section Rendering API, and swaps the grid, count,
   and both filter surfaces in place (URL synced with pushState).
   Load-more appends the next page's cards to the existing grid. */

const DESKTOP = '(min-width: 990px)';

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    // Sort select lives in the toolbar and is never replaced.
    this.sort = this.querySelector('[data-sort]');
    if (this.sort) {
      this.sort.addEventListener('change', () => this.apply());
    }

    // Delegated: inline dropdowns apply immediately; drawer waits for Apply.
    this.addEventListener('change', (event) => {
      if (event.target.closest('[data-facets-inline]')) this.apply();
    });
    this.addEventListener('click', (event) => {
      const applyBtn = event.target.closest('[data-apply-filters]');
      if (applyBtn) {
        event.preventDefault();
        this.apply();
        const drawer = this.querySelector('dotto-overlay');
        if (drawer) drawer.close();
        return;
      }
      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    });

    window.addEventListener('popstate', () => window.location.reload());
  }

  /* Inline and drawer inputs are duplicates; read only the surface the
     current viewport actually shows so stale values can't double-apply. */
  activeScope() {
    const desktop = window.matchMedia(DESKTOP).matches;
    return (
      this.querySelector(desktop ? '[data-facets-inline]' : '[data-facets-form]') ||
      this.querySelector('[data-facets-form]')
    );
  }

  buildQuery() {
    const params = new URLSearchParams();
    const scope = this.activeScope();
    if (scope) {
      scope.querySelectorAll('input').forEach((input) => {
        if (input.type === 'checkbox') {
          if (input.checked) params.append(input.name, input.value);
        } else if (input.value !== '') {
          params.append(input.name, input.value);
        }
      });
    }
    if (this.sort && this.sort.value) params.set('sort_by', this.sort.value);
    return params;
  }

  async apply() {
    const params = this.buildQuery();
    const url = `${window.location.pathname}?${params.toString()}`;
    await this.renderFrom(url);
    window.history.pushState({}, '', url);
  }

  async renderFrom(url) {
    const grid = this.querySelector('[data-product-grid]');
    if (grid) grid.style.opacity = '0.5';
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${this.sectionId}`);
    const html = await response.text();
    const fresh = new DOMParser().parseFromString(html, 'text/html');

    ['[data-product-grid]', '[data-product-count]', '[data-facets-inline]', '[data-facets-form]', '[data-load-more-wrap]', '.pagination'].forEach(
      (selector) => {
        const current = this.querySelector(selector);
        const incoming = fresh.querySelector(selector);
        if (current && incoming) current.replaceWith(incoming);
      }
    );
    this.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  }

  async loadMore(button) {
    button.setAttribute('aria-disabled', 'true');
    const url = new URL(button.href);
    url.searchParams.set('section_id', this.sectionId);
    const response = await fetch(url);
    const html = await response.text();
    const fresh = new DOMParser().parseFromString(html, 'text/html');

    const grid = this.querySelector('[data-product-grid]');
    const incomingGrid = fresh.querySelector('[data-product-grid]');
    if (grid && incomingGrid) {
      Array.from(incomingGrid.children).forEach((child) => {
        child.classList.add('is-visible');
        grid.appendChild(child);
      });
    }
    const wrap = this.querySelector('[data-load-more-wrap]');
    const incomingWrap = fresh.querySelector('[data-load-more-wrap]');
    if (wrap && incomingWrap) wrap.replaceWith(incomingWrap);
    else if (wrap) wrap.remove();
  }
}

if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
